import classNames from 'classnames';
import { FeedbackFields } from 'model';
import { useEffect, useState } from 'react';
import { useRateCommentMutation } from '../api/CommentApi';
import { useRatePostMutation } from '../api/PostApi';
import { IconButton } from './IconButton';
import {
	IconStar,
	IconStarSolid,
	IconThumbsDown,
	IconThumbsDownSolid,
	IconThumbsUp,
	IconThumbsUpSolid
} from './QuirkIcons';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';
import styles from './LikeDislike.module.css';
interface LikePostType {
	totalStars?: number;
	likeable: FeedbackFields;
}

const useLikable = (likeable: FeedbackFields, useMutation: any, name: string) => {
	const [disliked, setDislike] = useState(likeable.feedback < 0);
	const [liked, setLike] = useState(likeable.feedback > 0);
	const [triggerRating] = useMutation();
	const handleLike = () => {
		const newLike = !liked;
		let newDisliked = disliked;
		if (newLike) {
			newDisliked = false;
		}
		setLike(newLike);
		setDislike(newDisliked);
		triggerRating({ [name]: likeable, like: newLike, dislike: newDisliked });
	};
	const handleDislike = () => {
		const newDisliked = !disliked;
		let newLiked = liked;
		if (newDisliked) {
			newLiked = false;
		}
		setLike(newLiked);
		setDislike(newDisliked);
		triggerRating({ [name]: likeable, like: newLiked, dislike: newDisliked });
	};
	useEffect(() => {
		// Keep state in sync when likable changes
		setLike(likeable.feedback > 0);
		setDislike(likeable.feedback < 0);
	}, [likeable]);
	return { liked, disliked, handleLike, handleDislike };
};

type LikeButtonProps = {
	liked: boolean;
	disliked: boolean;
	handleLike: () => void;
	handleDislike: () => void;
};

const LikeButtons = ({ liked, disliked, handleLike, handleDislike }: LikeButtonProps) => (
	<div className={postStyles.buttonSpacers}>
		<IconButton
			icon={!disliked ? <IconThumbsDown size="large" /> : <IconThumbsDownSolid size="large" />}
			onClick={handleDislike}
		/>
		<IconButton
			icon={!liked ? <IconThumbsUp size="large" /> : <IconThumbsUpSolid size="large" />}
			onClick={handleLike}
		/>
	</div>
);

type StarAnswerProps = { liked: boolean; handleLike: () => void; totalStars?: number };

const StarAnswer = ({ liked, handleLike, totalStars = 0 }: StarAnswerProps) => (
	<div className={postStyles.buttonSpacers}>
		{totalStars > 0 && totalStars}
		<IconButton
			iconColor="#ffc409"
			icon={!liked ? <IconStar size="large" /> : <IconStarSolid size="large" />}
			onClick={handleLike}
		/>
	</div>
);

type InterestedButtonProps = { interested: boolean; handleInterested: () => void; count?: number };

const InterestedButton = ({ interested, handleInterested, count }: InterestedButtonProps) => (
	<div className={classNames(postStyles.buttonSpacers, styles.withEngagement)}>
		<span>Interested</span>
		<IconButton
			icon={!interested ? <IconThumbsUp size="large" /> : <IconThumbsUpSolid size="large" />}
			onClick={handleInterested}
			label={count}
		/>
	</div>
);

export const AnswerRate: React.FC<LikePostType> = ({ likeable, totalStars }) => {
	const { liked, handleLike } = useLikable(likeable, useRateCommentMutation, 'comment');
	return <StarAnswer totalStars={totalStars} liked={liked} handleLike={handleLike} />;
};

export const CommentLikeDislike: React.FC<LikePostType> = ({ likeable }) => {
	const { liked, disliked, handleLike, handleDislike } = useLikable(likeable, useRateCommentMutation, 'comment');
	return <LikeButtons liked={liked} disliked={disliked} handleLike={handleLike} handleDislike={handleDislike} />;
};

export const PostLikeDislike: React.FC<LikePostType> = ({ likeable }) => {
	const { liked, disliked, handleLike, handleDislike } = useLikable(likeable, useRatePostMutation, 'post');
	return <LikeButtons liked={liked} disliked={disliked} handleLike={handleLike} handleDislike={handleDislike} />;
};
export const EventLikeDislike: React.FC<LikePostType> = ({ likeable }) => {
	const { liked, handleLike } = useLikable(likeable, useRatePostMutation, 'post');
	return <InterestedButton interested={liked} handleInterested={handleLike} />;
};
